import { useEffect, useState, FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/feature/JsonLd';
import { useSEO } from '@/hooks/useSEO';
import { testimonials } from '@/mocks/articles';

const aboutJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '火影同人本子',
    url: 'https://huoying-tongren.com/',
    description: '火影同人本子是火影忍者粉丝二次创作的重要表达形式，涵盖漫画、小说、画册等多种形式。连接同人作者与读者，让每一份热爱都被看见。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
    sameAs: [
      'https://www.bilibili.com/v/douga/tongren/',
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 火影同人本子',
    description: '火影同人本子品牌介绍、创作者口碑与联系方式。连接火影同人创作者与读者，推动同人文化健康发展。',
    url: 'https://huoying-tongren.com/about',
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '火影同人本子·精选创作合集',
      url: 'https://huoying-tongren.com/',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://huoying-tongren.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
        item: 'https://huoying-tongren.com/about',
      },
    ],
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const location = useLocation();

  useSEO(
    '关于我们 | 火影同人本子·精选创作合集',
    '火影同人本子品牌介绍、创作者口碑与联系方式。火影同人本子是火影忍者粉丝二次创作的重要表达形式，涵盖漫画、小说、画册等多种形式。连接同人作者与读者。',
    '火影同人本子,关于我们,火影同人创作,同人社区,联系我们',
    'https://huoying-tongren.com/about',
  );

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace('#', ''));
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.hash]);

  useEffect(() => {
    let cancelled = false;
    async function fetchFormUrl() {
      try {
        const response = await fetch('https://readdy.ai/api/form/ensure', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: '联系我们 - 火影同人本子' }),
        });
        const data = await response.json();
        if (!cancelled && data?.submitAddr) {
          setFormUrl(data.submitAddr);
        }
      } catch {
        if (!cancelled) {
          setFormUrl('https://readdy.ai/api/form/submit/form_contact_huoying');
        }
      }
    }
    fetchFormUrl();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢你的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('idle');
    setFormMessage('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          body.append(key, String(value));
        }
      });

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      const code = (parsed as { code?: string })?.code;
      const metaMsg = (parsed as { meta?: { message?: string } })?.meta?.message;
      const serverMsg = metaMsg || (parsed as { message?: string })?.message || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢你的留言，我们会尽快回复！');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage(String(serverMsg || '提交失败，请稍后再试'));
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <JsonLd data={aboutJsonLd} />
      <Navbar />
      <main className="pt-16 bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">
                关于火影同人本子
              </h1>
              <p className="text-foreground-500 text-sm max-w-lg mx-auto">
                连接创作者与读者，让火影同人文化薪火相传
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-4">品牌故事</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  火影同人本子成立于2024年，由一群热爱《火影忍者》的同人创作者发起。我们深知同人创作者在发表作品时面临的困难——缺乏展示平台、难以触达目标读者、同人展会的时空限制等。因此，我们决定建立一个专属于火影同人爱好者的线上社区。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  两年来，我们从一个小小的同人作品分享站，成长为涵盖漫画、小说、画集、CP同人、活动资讯五大板块的综合平台。目前已有超过500位同人创作者入驻，累计收录优质火影同人作品1200余部。
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-6">
                <h2 className="font-heading text-xl text-foreground-950 mb-4">我们的使命</h2>
                <ul className="flex flex-col gap-4">
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg flex-shrink-0 mt-0.5">
                      <i className="ri-star-line text-background-50 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-900 mb-1">发现优质内容</h4>
                      <p className="text-xs text-foreground-500">通过编辑精选和智能推荐，帮助读者发现高质量的火影同人创作</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-accent-500 rounded-lg flex-shrink-0 mt-0.5">
                      <i className="ri-brush-line text-background-50 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-900 mb-1">支持创作者</h4>
                      <p className="text-xs text-foreground-500">为同人创作者提供展示平台、曝光机会和读者互动渠道</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-secondary-500 rounded-lg flex-shrink-0 mt-0.5">
                      <i className="ri-group-line text-background-50 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-900 mb-1">建设社区文化</h4>
                      <p className="text-xs text-foreground-500">组织线上线下同人活动，促进同好交流，推动同人文化健康发展</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              <div className="bg-background-100 rounded-xl p-5 text-center">
                <div className="text-2xl font-bold text-primary-500 mb-1">500+</div>
                <p className="text-xs text-foreground-500">入驻创作者</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 text-center">
                <div className="text-2xl font-bold text-accent-500 mb-1">1200+</div>
                <p className="text-xs text-foreground-500">收录作品</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 text-center">
                <div className="text-2xl font-bold text-primary-500 mb-1">50万+</div>
                <p className="text-xs text-foreground-500">月均阅读量</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 text-center">
                <div className="text-2xl font-bold text-accent-500 mb-1">30+</div>
                <p className="text-xs text-foreground-500">合作展会</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                创作者口碑
              </h2>
              <p className="text-foreground-500 text-sm">
                听听同人创作者们怎么说
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-6 border border-background-200">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      title={`${t.name} - ${t.role}`}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Submit Guidelines */}
        <section id="submit" className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                投稿指南
              </h2>
              <p className="text-foreground-500 text-sm max-w-lg mx-auto">
                想要分享你的火影同人创作？我们欢迎所有形式的原创同人作品投稿
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              <div className="bg-background-100 rounded-xl p-6 text-center border border-background-200">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-full mx-auto mb-4">
                  <i className="ri-file-add-line text-background-50 text-xl"></i>
                </div>
                <h4 className="font-heading text-base text-foreground-950 mb-2">准备作品</h4>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  整理你的火影同人作品（漫画、小说或画集），准备至少3张预览图和作品简介
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center border border-background-200">
                <div className="w-12 h-12 flex items-center justify-center bg-accent-500 rounded-full mx-auto mb-4">
                  <i className="ri-mail-send-line text-background-50 text-xl"></i>
                </div>
                <h4 className="font-heading text-base text-foreground-950 mb-2">提交审核</h4>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  通过下方联系表单或投稿邮箱发送作品资料，编辑团队7个工作日内审核回复
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center border border-background-200">
                <div className="w-12 h-12 flex items-center justify-center bg-secondary-500 rounded-full mx-auto mb-4">
                  <i className="ri-rocket-line text-background-50 text-xl"></i>
                </div>
                <h4 className="font-heading text-base text-foreground-950 mb-2">发布推广</h4>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  通过审核后作品即刻上线，获得首页推荐位和专题报道曝光机会
                </p>
              </div>
            </div>

            <div className="bg-background-100 rounded-xl p-6 border border-background-200">
              <h4 className="font-heading text-base text-foreground-950 mb-3">投稿须知</h4>
              <ul className="flex flex-col gap-2 text-sm text-foreground-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                  <span>投稿作品需为原创火影同人创作，严禁抄袭、盗用他人作品</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                  <span>支持漫画、小说、画集等多种形式，内容需符合社区规范</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                  <span>投稿即表示同意作品在本站展示，版权仍归创作者所有</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                  <span>通过审核的作品可随时申请下架或更新，我们尊重创作者的决定</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm">
                有任何问题或合作意向，欢迎给我们留言
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form="true"
              className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200"
            >
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="你的名字"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="留言主题（选填）"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                    placeholder="请输入你想说的话（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <div className="honeypot-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-line"></i>
                  发送留言
                </button>

                {formStatus === 'success' && (
                  <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-sm flex items-center gap-2">
                    <i className="ri-check-line"></i>
                    {formMessage}
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-sm flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    {formMessage}
                  </div>
                )}
              </div>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}